import React, { useState, useEffect } from 'react';
import { 
  getFirestore, 
  doc, 
  getDoc, 
  collection, 
  addDoc, 
  updateDoc, 
  arrayUnion, 
  serverTimestamp,
  query, // Added
  where, // Added
  getDocs, // Added
  deleteDoc // <-- Added: Import deleteDoc
} from 'firebase/firestore';
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import {
  PlusIcon,
  DocumentTextIcon,
  ClockIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import PrequalificationDetailsView from './PrequalificationDetailsView';
import { getAuth, getIdToken } from 'firebase/auth'; // Added for document processing API

// Initialize Firestore and Storage
const db = getFirestore();
const storage = getStorage();
const auth = getAuth(); // Added for document processing API

const PrequalificationsTab = ({
  currentUser,
  prequalificationApplications,
  setPrequalificationApplications,
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  selectedApplication,
  setSelectedApplication,
  showBusinessPrequalForm,
  setShowBusinessPrequalForm,
  handleNewBusiness,
  showDeleteConfirmation,
  getStatusBadgeClass,
  formatCurrency,
  formatDate,
  customLogo
}) => {
  // Add state for view type
  const [prequalViewType, setPrequalViewType] = useState('table'); // 'table' or 'card'

  // Add new state
  const [showPrequalifiedView, setShowPrequalifiedView] = useState(false);

  // Add a new state to track if we're viewing details
  const [viewingDetails, setViewingDetails] = useState(false);

  // Add new state variables
  const [prequalId, setPrequalId] = useState(null);
  const [activeStep, setActiveStep] = useState(1); // Track the active step
  
  // State to track status of individual uploads: { [key]: 'idle' | 'uploading' | 'uploaded' | 'error' | 'not_available', ... }
  // Key format: `${documentId}-${label}`
  const [uploadStatuses, setUploadStatuses] = useState({});

  // State for custom notifications
  const [notification, setNotification] = useState({ visible: false, type: '', message: '' });

  // State for accordion index
  const [openAccordionIndex, setOpenAccordionIndex] = useState(null);

  // State for loading indicator when fetching a single application for edit
  const [isLoadingApplication, setIsLoadingApplication] = useState(false);

  // Calculate summary statistics
  const totalApplications = prequalificationApplications.length;
  const totalPrequalifiedAmount = prequalificationApplications.reduce(
    (sum, app) => sum + (Number(app.prequalifiedAmount) || 0),
    0
  );
  const applicationsWithQuestions = prequalificationApplications.filter(
    app => app.status === 'questions' // New status for questions
  ).length;

  // Fetch prequalification applications
  const fetchPrequalificationApplications = async () => {
    if (!currentUser) return;
    
    try {
      const brokerDocRef = doc(db, 'brokers', currentUser.uid);
      const brokerDoc = await getDoc(brokerDocRef);
      
      if (brokerDoc.exists() && brokerDoc.data().prequalificationApplications) {
        const prequalIds = brokerDoc.data().prequalificationApplications;
        
        const prequalDocs = [];
        for (const prequalId of prequalIds) {
          const prequalDocRef = doc(db, 'prequalificationApplications', prequalId);
          const prequalDoc = await getDoc(prequalDocRef);
          
          if (prequalDoc.exists()) {
            prequalDocs.push({
              id: prequalDoc.id,
              ...prequalDoc.data()
            });
          }
        }
        
        setPrequalificationApplications(prequalDocs);
      }
    } catch (error) {
      console.error('Error fetching prequalification applications:', error);
    }
  };

  // Filter prequalification applications based on search and status
  const filteredPrequalifications = prequalificationApplications
    .filter(app => {
      // Filter by search query if one exists
      if (searchQuery && (!app.businessName || !app.businessName.toLowerCase().includes(searchQuery.toLowerCase()))) {
        return false;
      }
      
      // Filter by status if not "All"
      if (statusFilter !== 'All' && app.status !== statusFilter) {
        return false;
      }
      
      return true;
    });

  console.log('Filtered prequalifications:', filteredPrequalifications);

  // Modify the handleEdit function to show details instead of the form when prequalified
  // Also, fetch the latest data for the application before editing.
  const handleEdit = async (applicationId) => {
    setIsLoadingApplication(true); // Start loading indicator
    try {
      const appDocRef = doc(db, 'prequalificationApplications', applicationId);
      const appDoc = await getDoc(appDocRef);

      if (appDoc.exists()) {
        const applicationData = { id: appDoc.id, ...appDoc.data() };
        setSelectedApplication(applicationData);
      
      // If the application has a prequalifiedAmount, show details view instead of edit form
        if (applicationData.prequalifiedAmount) {
        setViewingDetails(true);
        setShowBusinessPrequalForm(false);
      } else {
        setViewingDetails(false);
        setShowBusinessPrequalForm(true);
      }
      } else {
        console.error("Application not found:", applicationId);
        // Optionally, show a notification to the user
        setNotification({ visible: true, type: 'error', message: 'Could not load application data. It may have been deleted.' });
        setTimeout(() => setNotification({ visible: false, type: '', message: '' }), 3000);
      }
    } catch (error) {
      console.error("Error fetching application for edit:", error);
      // Optionally, show an error notification
      setNotification({ visible: true, type: 'error', message: 'Error loading application details.' });
      setTimeout(() => setNotification({ visible: false, type: '', message: '' }), 3000);
    } finally {
      setIsLoadingApplication(false); // Stop loading indicator
    }
  };

  // Add a function to go back from details view
  const handleBackFromDetails = () => {
    setViewingDetails(false);
    setSelectedApplication(null);
  };

  // Add new PrequalifiedView component
  const PrequalifiedView = ({ application, onBack, formatCurrency }) => {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="max-w-3xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-extrabold text-gray-900">
                {application.businessName || 'Unnamed Business'}
              </h2>
              <button
                onClick={onBack}
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Back
              </button>
            </div>

            {/* Prequalified Amount Box */}
            <div className="border-2 border-dotted border-green-500 rounded-lg p-6 mb-8 bg-green-50">
              <div className="text-center">
                <h3 className="text-lg font-medium text-green-800 mb-2">Prequalified Amount</h3>
                <p className="text-3xl font-bold text-green-700">
                  {formatCurrency(application.prequalifiedAmount)}
                </p>
              </div>
            </div>

            {/* Add Potential Borrower Button */}
            <button
              className="w-full border-2 border-dotted border-gray-300 rounded-lg p-6 hover:bg-gray-50 transition-colors"
              onClick={() => {
                // Add your logic for handling potential borrower here
                console.log("Add potential borrower clicked");
              }}
            >
              <div className="text-center">
                <PlusIcon className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <h3 className="text-lg font-medium text-gray-900">Add Potential Borrower</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Click here to add a potential borrower for this prequalification
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (showPrequalifiedView && selectedApplication) {
    return (
      <PrequalifiedView
        application={selectedApplication}
        onBack={() => {
          setShowPrequalifiedView(false);
          setSelectedApplication(null);
        }}
        formatCurrency={formatCurrency}
      />
    );
  }

  if (viewingDetails && selectedApplication) {
    return (
      <PrequalificationDetailsView 
        application={selectedApplication} 
        onBack={() => {
          setViewingDetails(false);
          setSelectedApplication(null);
        }}
        customLogo={customLogo}
      />
    );
  }

  if (showBusinessPrequalForm) {
    return (
      <BusinessPrequalificationForm 
        onCancel={() => {
          setShowBusinessPrequalForm(false);
          setSelectedApplication(null);
          // Refresh prequalification applications when returning from the form
          fetchPrequalificationApplications();
        }}
        currentUser={currentUser}
        initialData={selectedApplication}
      />
    );
  }

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      {/* Add a simple loading indicator for when an application is being fetched for edit */}
      {isLoadingApplication && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded-lg shadow-xl">
            <p className="text-gray-700">Loading application data...</p>
            {/* You can add a spinner here */}
          </div>
        </div>
      )}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-bold leading-tight text-gray-800">Prequalifications</h1>
          <p className="mt-1 text-md text-gray-500">
            Manage all prequalification applications.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex sm:space-x-4">
          {/* Add view toggle buttons */}
          <div className="inline-flex rounded-md shadow-sm mr-4" role="group">
            <button
              type="button"
              onClick={() => setPrequalViewType('table')}
              className={`px-4 py-2 text-sm font-medium rounded-l-md ${
                prequalViewType === 'table' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5 4a3 3 0 00-3 3v6a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H5zm-1 9v-1h5v2H5a1 1 0 01-1-1zm7 1h4a1 1 0 001-1v-1h-5v2zm0-4h5V8h-5v2zM9 8H4v2h5V8z" clipRule="evenodd" />
              </svg>
              Table
            </button>
            <button
              type="button"
              onClick={() => setPrequalViewType('card')}
              className={`px-4 py-2 text-sm font-medium rounded-r-md ${
                prequalViewType === 'card' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              Cards
            </button>
          </div>
          <button
            onClick={handleNewBusiness}
            className="rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Add prequalification
          </button>
        </div>
      </div>

      {/* Summary Statistics Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Total Applications</h3>
          <p className="mt-1 text-3xl font-semibold text-indigo-600">{totalApplications}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Total Prequalified Value</h3>
          <p className="mt-1 text-3xl font-semibold text-green-600">{formatCurrency(totalPrequalifiedAmount)}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg border border-yellow-400 bg-yellow-50">
          <h3 className="text-sm font-medium text-yellow-700">Applications with Questions</h3>
          <p className="mt-1 text-3xl font-semibold text-yellow-600">{applicationsWithQuestions}</p>
        </div>
      </div>

      {/* Search and Filter Section - Updated Design */}
      <div className="my-8 p-6 bg-white shadow-lg rounded-xl border border-gray-200">
        <div className="md:flex md:items-end md:space-x-6 space-y-4 md:space-y-0">
          {/* Search Bar */}
          <div className="flex-grow">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Search business names
            </label>
            <div className="relative">
              <input
                type="text"
                name="search"
                id="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm py-2 pl-3 pr-10 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="Enter business name..."
              />
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          {/* Status Filter */}
          <div className="md:w-auto md:min-w-[200px]"> {/* Ensure filter has enough space */}
            <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Filter by status
            </label>
            <select
              id="status-filter"
              name="status-filter"
              className="block w-full rounded-md border-gray-300 shadow-sm py-2 pl-3 pr-10 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Statuses</option>
              <option value="applicationstarted">Application Started</option>
              <option value="underReview">Under Review</option>
              <option value="questions">Questions</option>
              <option value="preQualified">Prequalified</option>
            </select>
          </div>
        </div>
      </div>

      {/* Empty state */}
      {filteredPrequalifications.length === 0 && (
        <div className="text-center py-10 mt-8">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No prequalifications</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new prequalification application.
          </p>
          <div className="mt-6">
            <button
              type="button"
              onClick={handleNewBusiness}
              className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
              New Prequalification
            </button>
          </div>
        </div>
      )}

      {/* Table View */}
      {filteredPrequalifications.length > 0 && prequalViewType === 'table' && (
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                      Business Name
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Prequalified Amount
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Last Updated
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredPrequalifications.map((application) => (
                    <tr key={application.id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                        {application.businessName || 'Unnamed Business'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusBadgeClass(application.status || 'applicationstarted')}`}>
                          {application.status || 'applicationstarted'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {application.prequalifiedAmount ? formatCurrency(application.prequalifiedAmount) : 'N/A'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {application.lastUpdated ? formatDate(application.lastUpdated) : 'N/A'}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                        <button
                          onClick={() => handleEdit(application.id)}
                          className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10 mr-2"
                        >
                          View details
                        </button>
                        <button
                          onClick={() => showDeleteConfirmation(application)}
                          className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-700/10"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Card View */}
      {filteredPrequalifications.length > 0 && prequalViewType === 'card' && (
        <div className="mt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPrequalifications.map((application) => (
              <div 
                key={application.id}
                className={`w-full rounded-xl overflow-hidden shadow-lg border ${
                  application.prequalifiedAmount 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-gray-200 bg-white hover:border-blue-300 transition-colors duration-200'
                } cursor-pointer`}
                onClick={() => handleEdit(application.id)}
              >
                <div className={`p-6 border-b ${
                  application.prequalifiedAmount 
                    ? 'border-emerald-200 bg-emerald-50' 
                    : 'border-gray-100 bg-gradient-to-r from-white to-blue-50'
                }`}>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold text-gray-800 truncate">
                      {application.businessName || 'Unnamed Business'}
                    </h3>
                    <div className="flex items-center gap-2">
                      <img src={customLogo} alt="Company Logo" className="w-5 h-5" />
                    </div>
                  </div>
                  <div className="flex items-center text-gray-600 text-sm mb-3">
                    <MapPinIcon className="w-4 h-4 mr-2 text-blue-500" />
                    <p>{application.businessState || 'Location not specified'}</p>
                  </div>
                  <div className="border-2 border-dotted border-gray-300 rounded-md p-2 text-sm mb-3 bg-white bg-opacity-70">
                    <span className="text-gray-600 font-medium">Status: </span>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusBadgeClass(application.status || 'applicationstarted')}`}>
                      {application.status || 'applicationstarted'}
                    </span>
                  </div>
                </div>
                
                <div className={`p-6 space-y-4 ${
                  application.prequalifiedAmount 
                    ? 'bg-emerald-50' 
                    : 'bg-white'
                }`}>
                  <div className="rounded-lg p-4 space-y-2 bg-gray-50 shadow-sm hover:shadow transition-shadow duration-200">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 font-medium">Prequalified Amount</span>
                      <div className="flex items-center">
                        <CurrencyDollarIcon className="w-4 h-4 mr-2 text-blue-500" />
                        <span className={`font-semibold ${
                          application.prequalifiedAmount 
                            ? 'text-emerald-700' 
                            : 'text-gray-500'
                        }`}>
                          {application.prequalifiedAmount 
                            ? formatCurrency(application.prequalifiedAmount)
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-lg p-4 space-y-2 bg-blue-50 shadow-sm hover:shadow transition-shadow duration-200">
                    <div className="flex items-center justify-between">
                      <span className="text-blue-700 font-medium">Documents Uploaded</span>
                      <div className="flex items-center text-gray-800">
                        <DocumentTextIcon className="w-4 h-4 mr-2 text-blue-500" />
                        <span className="bg-white px-2 py-1 rounded-md text-sm">
                          {application.documents ? application.documents.length : 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className={`px-6 py-4 flex items-center justify-between ${
                  application.prequalifiedAmount 
                    ? 'bg-emerald-100' 
                    : 'bg-gray-50'
                }`}>
                  <span className="text-gray-600 text-sm">Last Updated</span>
                  <div className="flex items-center text-gray-700 text-sm">
                    <ClockIcon className="w-4 h-4 mr-2 text-blue-500" />
                    {application.lastUpdated ? formatDate(application.lastUpdated) : 'N/A'}
                  </div>
                </div>
                
                <div className={`px-6 py-4 flex items-center justify-between ${
                  application.prequalifiedAmount 
                    ? 'bg-emerald-100/70' 
                    : 'bg-gray-100'
                }`}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(application.id);
                    }}
                    className="inline-flex items-center rounded-md bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10 hover:bg-blue-100 transition-colors duration-200"
                  >
                    {application.prequalifiedAmount ? 'View details' : 'Edit'}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      showDeleteConfirmation(application);
                    }}
                    className="inline-flex items-center rounded-md bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-700/10 hover:bg-red-100 transition-colors duration-200"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Custom Notification Area */}
      {notification.visible && (
        <div 
          className={`fixed bottom-5 right-5 p-4 rounded-lg shadow-lg text-white z-50 transition-opacity duration-300 ${ 
            notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          }`}
        >
          {notification.message}
        </div>
      )}
    </div>
  );
};

// Business Prequalification Form Component
const BusinessPrequalificationForm = ({ onCancel, currentUser, initialData }) => {
  const [businessName, setBusinessName] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [businessAge, setBusinessAge] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [licenseNeeded, setLicenseNeeded] = useState(false);
  // uploadedDocuments state will now store metadata fetched from prequalificationMetadata collection
  const [uploadedDocuments, setUploadedDocuments] = useState([]); 
  const [isLoading, setIsLoading] = useState(false);
  const [prequalId, setPrequalId] = useState(null);
  const [activeStep, setActiveStep] = useState(1); // Track the active step
  const [realEstateIncluded, setRealEstateIncluded] = useState(null); // 'yes' or 'no', or null if unanswered
  const [realEstateValue, setRealEstateValue] = useState('');
  const [hasOtherAddbacks, setHasOtherAddbacks] = useState(null); // 'yes' or 'no', or null if unanswered
  const [topCustomerConcentration, setTopCustomerConcentration] = useState(null); // Changed to null for 'yes' or 'no', or null if unanswered
  const [hasMultipleLLCs, setHasMultipleLLCs] = useState(null); // 'yes' or 'no', or null if unanswered - New State

  // State to track status of individual uploads: { [key]: 'idle' | 'uploading' | 'uploaded' | 'error' | 'not_available', ... }
  // Key format: `${documentId}-${label}`
  const [uploadStatuses, setUploadStatuses] = useState({});

  // State for custom notifications
  const [notification, setNotification] = useState({ visible: false, type: '', message: '' });
  
  // State to track the open accordion item index
  const [openAccordionIndex, setOpenAccordionIndex] = useState(null); // null means all are closed

  // Add autosave debounce timer state
  const [saveTimer, setSaveTimer] = useState(null);
  // Add saving indicator state
  const [autoSaving, setAutoSaving] = useState(false);

  // State for submission readiness and process
  const [isSubmittable, setIsSubmittable] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Business prequalification documents
  const businessDocuments = [
    {
      DocumentID: 1,
      displayName: "2022 Business Tax Returns",
      documentName: "Business Tax Returns",
      Description: "Complete business tax returns for 2022",
      Labels: ["2022 Business Tax Return", "Set 2 - 2022 Business Tax Return"],
      AcceptedFileTypes: ".pdf,.jpg,.png"
    },
    {
      DocumentID: 2,
      displayName: "2023 Business Tax Returns",
      documentName: "Business Tax Returns",
      Description: "Complete business tax returns for 2023",
      Labels: ["2023 Business Tax Return", "Set 2 - 2023 Business Tax Return"],
      AcceptedFileTypes: ".pdf,.jpg,.png"
    },
    {
      DocumentID: 3,
      displayName: "2024 Business Tax Returns",
      documentName: "Business Tax Returns",
      Description: "Complete business tax returns for 2024",
      Labels: ["2024 Business Tax Return", "Set 2 - 2024 Business Tax Return"],
      AcceptedFileTypes: ".pdf,.jpg,.png"
    },
    {
      DocumentID: 4,
      displayName: "2025 YTD Profit & Loss",
      documentName: "YTD PnL",
      Description: "Year-to-date Profit and Loss statement for 2025",
      Labels: ["2025 YTD P&L"],
      AcceptedFileTypes: ".pdf,.xls,.xlsx,.jpg,.png"
    },
    {
      DocumentID: 5,
      displayName: "2025 YTD Balance Sheet",
      documentName: "Balance Sheet",
      Description: "Year-to-date Balance Sheet for 2025",
      Labels: ["2025 YTD Balance Sheet"],
      AcceptedFileTypes: ".pdf,.xls,.xlsx,.jpg,.png"
    },
    {
      DocumentID: 6,
      displayName: "Confidential Information Memorandum",
      documentName: "CIM",
      Description: "CIM if available",
      Labels: ["CIM Document"],
      AcceptedFileTypes: ".pdf,.doc,.docx"
    }
  ];

  // Define conditional documents meta data - These will be added dynamically
  const conditionalDocuments = {
    license: {
      DocumentID: 101, // Assign unique IDs
      displayName: "Business License",
      documentName: "Other", // No specific category for business license in the list
      Description: "Upload the relevant business license if required",
      Labels: ["Business License"],
      AcceptedFileTypes: ".pdf,.jpg,.png,.doc,.docx"
    },
    appraisal: {
      DocumentID: 102,
      displayName: "Real Estate Appraisal",
      documentName: "Other", // No specific category for real estate appraisal in the list
      Description: "Upload the real estate appraisal if applicable",
      Labels: ["Real Estate Appraisal"],
      AcceptedFileTypes: ".pdf"
    },
    addbacks: {
      DocumentID: 103,
      displayName: "Addbacks Schedule",
      documentName: "AddBack Sheet",
      Description: "Upload a schedule detailing non-standard addbacks (must be verifiable)",
      Labels: ["Addbacks Schedule"],
      AcceptedFileTypes: ".pdf,.xls,.xlsx,.doc,.docx"
    },
    customerConcentration: {
      DocumentID: 104,
      displayName: "Customer Concentration Details",
      documentName: "Customer Concentration",
      Description: "Upload a list of top 10 customers by % of revenue and/or description of any concentrations",
      Labels: ["Top 10 Customer List"],
      AcceptedFileTypes: ".pdf,.xls,.xlsx,.doc,.docx"
    }
  };

  // Define Other Important Documentation - Added
  const otherDocuments = {
    other: {
      DocumentID: 999, // Assign a unique ID
      displayName: "Other Important Documentation",
      documentName: "Other",
      Description: "Upload any other relevant documents",
      Labels: ["Other Important Documents"], // Use a single label for now
      AcceptedFileTypes: "*/*" // Allow any file type
    }
  };

  const formatCurrency = (value) => {
    // Check if value is a string, if not convert it to a string
    const stringValue = String(value || '');
    // Remove all non-digits
    const digits = stringValue.replace(/\D/g, '');
    // Format with commas
    return digits ? `$${Number(digits).toLocaleString()}` : '';
  };

  // Function to show notifications
  const showNotification = (type, message) => {
    setNotification({ visible: true, type, message });
    setTimeout(() => {
      setNotification({ visible: false, type: '', message: '' });
    }, 3000); // Hide after 3 seconds
  };

  // Function to fetch metadata from the new collection
  const fetchPrequalificationMetadata = async (applicationId) => {
    if (!applicationId) return;
    console.log("Fetching metadata for application:", applicationId);
    try {
      const metadataQuery = query(
        collection(db, 'prequalificationMetadata'), 
        where('applicationId', '==', applicationId)
      );
      const querySnapshot = await getDocs(metadataQuery);
      const fetchedMetadata = [];
      querySnapshot.forEach((doc) => {
        fetchedMetadata.push({ id: doc.id, ...doc.data() }); // Include Firestore doc ID if needed
      });
      console.log("Fetched metadata:", fetchedMetadata);
      setUploadedDocuments(fetchedMetadata); // Update state with fetched data
    } catch (error) {
      console.error('Error fetching prequalification metadata:', error);
      showNotification('error', 'Failed to load document statuses.');
    }
  };

  // *** Added Helper Function ***
  // Maps specific document definitions in this component to a general category name
  const getGeneralDocumentName = (document) => {
    if (!document || !document.Name) return 'Other'; // Default safety

    const name = document.Name.toLowerCase();
    const id = document.DocumentID;

    // Check specific IDs or name patterns
    if (id === 1 || id === 2 || id === 3 || name.includes('business tax return')) {
      return 'Business Tax Returns';
    }
    if (id === 4 || name.includes('profit & loss') || name.includes('p&l')) {
      return 'PnL';
    }
    if (id === 5 || name.includes('balance sheet')) {
      return 'Balance Sheet';
    }
    if (id === 6 || name.includes('confidential information memorandum') || name.includes('cim')) {
      return 'CIM';
    }
    if (id === conditionalDocuments.addbacks.DocumentID || name.includes('addbacks schedule')) {
      return 'AddBack Sheet';
    }
    if (id === conditionalDocuments.customerConcentration.DocumentID || name.includes('customer concentration details')) {
      return 'Customer Concentration';
    }
     if (id === otherDocuments.other.DocumentID || name.includes('other important documentation')) {
      return 'Other';
    }
    // For types not explicitly mapped in the user's list, use their specific Name for now
    if (id === conditionalDocuments.license.DocumentID || name.includes('business license')) {
       return 'Business License'; // Keeping specific as per analysis
    }
    if (id === conditionalDocuments.appraisal.DocumentID || name.includes('real estate appraisal')) {
       return 'Real Estate Appraisal'; // Keeping specific as per analysis
    }

    // Fallback if none match - use the specific name defined in the component
    console.warn(`Unmapped document type for general name: ${document.Name} (ID: ${id}). Using specific name.`);
    return document.Name; 
  };
  // *** End Helper Function ***

  const handleFileUpload = async (event, documentId, label) => {
    if ((!event.target.files || !event.target.files[0]) && (!event.dataTransfer || !event.dataTransfer.files || !event.dataTransfer.files[0])) {
      console.log("No file provided in event");
      return; // Exit if no file is found in either target or dataTransfer
    }
    if (!prequalId) {
      console.log("Prequalification ID not set");
      return; // Exit if prequalId is not set
    }

    const file = event.target.files ? event.target.files[0] : event.dataTransfer.files[0];
    
    // --- Fix: Search both standard and conditional documents ---
    let document = businessDocuments.find(doc => doc.DocumentID === documentId);
    if (!document) {
      document = Object.values(conditionalDocuments).find(doc => doc.DocumentID === documentId);
    }
    // --- Modification Start: Add check for otherDocuments ---
    if (!document && documentId === otherDocuments.other.DocumentID) {
      document = otherDocuments.other;
    }
    // --- Modification End ---

    if (!document) {
      console.error("Document definition not found for ID:", documentId);
      showNotification('error', `Internal error: Document definition not found.`);
      return; // Exit if document definition is not found
    }

    const uploadKey = `${documentId}-${label}`;

    // Set status to uploading
    setUploadStatuses(prev => ({ ...prev, [uploadKey]: 'uploading' }));
    setIsLoading(true); // Keep general loading state for the submit button
    
    try {
      // Create storage path with prequalId for organization
      const storageRef = ref(storage, 
        `prequalifications/${currentUser.uid}/${prequalId}/${document.displayName}/${label}/${file.name}`
      );

      // Upload file
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Create metadata for the new collection
      const metadata = {
        applicationId: prequalId,
        brokerId: currentUser.uid,
        // --- Updated: Use direct documentName property ---
        documentName: document.documentName, 
        fileName: file.name,
        fileUrl: downloadURL,
        // --- Label remains the specific one ---
        label: label, 
        uploadedAt: new Date().toISOString(), // As per user example
        status: 'uploaded' // Explicit status
      };

      // --- Refactor Start: Save metadata to new collection ---
      // Remove previous logic of adding to local state immediately
      // Add the metadata document to the 'prequalificationMetadata' collection
      const docRef = await addDoc(collection(db, 'prequalificationMetadata'), metadata);
      console.log(`Metadata saved to prequalificationMetadata for ${label} with ID: ${docRef.id}`);
      // --- Refactor End ---
      
      // Update the lastUpdated timestamp on the main application document
      const prequalRef = doc(db, 'prequalificationApplications', prequalId);
      await updateDoc(prequalRef, {
        lastUpdated: serverTimestamp()
      });

      // Set status to uploaded
      setUploadStatuses(prev => ({ ...prev, [uploadKey]: 'uploaded' }));

      // --- Add API Integration for Document Processing ---
      try {
        // Prepare request data
        const requestBody = {
          fileUrl: downloadURL,
          metadata: {
            ...metadata,
            documentId: docRef.id, // Include the Firestore document ID
            documentType: document.documentName // Include document type for the API
          }
        };
        
        // Get auth token
        const idToken = await getIdToken(auth.currentUser);
        
        // Make API call to process document
        const apiEndpoint = 'https://sba2-7f2ba300d905.herokuapp.com/process-document'; // Using the one from documentTracker.js
        console.log('Sending document for processing:', { ...requestBody, fileUrl: '[URL REDACTED]' });
        
        const response = await fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });
        
        console.log('API response status:', response.status);
        
        if (response.ok) {
          console.log('Document processed successfully');
          const responseData = await response.json();
          console.log('Processing result:', responseData);
          
          // You could update metadata here with extracted data if needed
          
          showNotification('success', `'${label}' uploaded and processed successfully!`);
        } else {
          console.error('Error processing document:', await response.text());
          // Still show a success message for upload since the file was uploaded successfully
          showNotification('success', `'${label}' uploaded successfully, but processing failed.`);
        }
      } catch (apiError) {
        console.error('API processing error:', apiError);
        // We don't want to fail the whole upload if only the processing fails
        showNotification('success', `'${label}' uploaded successfully, but processing failed: ${apiError.message}`);
      }
      // --- End API Integration ---

      // --- Refactor: Fetch updated metadata to refresh state ---
      await fetchPrequalificationMetadata(prequalId);

    } catch (error) {
      console.error('Error uploading file:', error);
      // Set status to error and show error notification
      setUploadStatuses(prev => ({ ...prev, [uploadKey]: 'error' }));
      showNotification('error', `Error uploading '${label}'. Please try again.`);
    } finally {
      // Check if any uploads are still in progress before setting isLoading to false
      // We need to check uploadStatuses *after* potential state updates from fetch
      const stillUploading = Object.values(uploadStatuses).some(status => status === 'uploading');
       if (!stillUploading) {
          // Re-evaluate loading state after potential fetch
          const currentStatuses = { ...uploadStatuses }; // Get potentially updated statuses
          const isLoadingStill = Object.values(currentStatuses).some(s => s === 'uploading');
          setIsLoading(isLoadingStill);
       }
    }
  };

  // Handle clicking "Do Not Have"
  const handleDoNotHave = async (documentId, label) => { // Made async
    // Find the document definition (could be in businessDocuments or conditionalDocuments)
    // --- Modification Start: Include otherDocuments ---
    let document = businessDocuments.find(doc => doc.DocumentID === documentId);
    if (!document) {
      document = Object.values(conditionalDocuments).find(doc => doc.DocumentID === documentId);
    }
    if (!document && documentId === otherDocuments.other.DocumentID) {
      document = otherDocuments.other;
    }
    // --- Modification End ---

    if (!document) {
      console.error("Document definition not found for ID:", documentId);
      return; // Should not happen if IDs are correct
    }

    const uploadKey = `${documentId}-${label}`;

    // Set status in uploadStatuses state
    setUploadStatuses(prev => ({ ...prev, [uploadKey]: 'not_available' }));

    // Create metadata for the 'not_available' status for the new collection
    const metadata = {
      applicationId: prequalId,
      brokerId: currentUser.uid,
      // --- Updated: Use direct documentName property ---
      documentName: document.documentName,
      fileName: null, // No file
      fileUrl: null, // No URL
      // --- Label remains the specific one ---
      label: label, 
      markedAt: new Date().toISOString(), // Use markedAt
      status: 'not_available'
    };

    // --- Refactor Start: Save "Not Available" status to new collection ---
    // Remove previous logic of adding to local state immediately
     try {
       // Check if a record for this label already exists (uploaded or N/A) and remove/update if necessary,
       // or decide on strategy (e.g., allow multiple N/A entries vs overwrite).
       // For simplicity here, we'll just add a new entry. Consider adding cleanup/overwrite logic if needed.
       await addDoc(collection(db, 'prequalificationMetadata'), metadata);
       console.log(`'Not Available' status saved to prequalificationMetadata for ${label}`);

       // Update the lastUpdated timestamp on the main application document
       const prequalRef = doc(db, 'prequalificationApplications', prequalId);
       await updateDoc(prequalRef, {
         lastUpdated: serverTimestamp()
       });

       // Optional: Show a notification
       showNotification('info', `'${label}' marked as 'Not Available'.`);

       // Fetch updated metadata
       await fetchPrequalificationMetadata(prequalId);

     } catch (error) {
       console.error("Error saving 'Not Available' status:", error);
       showNotification('error', `Failed to mark '${label}' as Not Available.`);
       // Revert local status indicator if save failed
       setUploadStatuses(prev => ({ ...prev, [uploadKey]: 'idle' })); // Or previous state
     }
    // --- Refactor End ---
  };

  // isDocumentUploaded and isDocumentNotAvailable now rely on the fetched uploadedDocuments state
  const isDocumentUploaded = (documentId, label) => {
    // --- Modification Start: Include otherDocuments & check 'some' for 'Other' ---
    let document = businessDocuments.find(doc => doc.DocumentID === documentId);
     if (!document) {
       document = Object.values(conditionalDocuments).find(doc => doc.DocumentID === documentId);
     }
     if (!document && documentId === otherDocuments.other.DocumentID) {
       document = otherDocuments.other;
     }
     if (!document) return false;

     if (documentId === otherDocuments.other.DocumentID) {
       // For 'Other', check if at least one document with 'uploaded' status exists for this label
       return uploadedDocuments.some(
         doc => doc.documentName === document.documentName && doc.label === label && doc.status === 'uploaded'
       );
     } else {
       // For other types, check if the specific entry exists and is 'uploaded'
       const docEntry = uploadedDocuments.find(
         doc => doc.documentName === document.documentName && doc.label === label && doc.status === 'uploaded'
       );
       return !!docEntry; // Return true if such an entry exists
     }
     // --- Modification End ---
  };

  // Check if a document is marked as 'Not Available'
  const isDocumentNotAvailable = (documentId, label) => {
     // --- Modification Start: Include otherDocuments ---
     let document = businessDocuments.find(doc => doc.DocumentID === documentId);
     if (!document) {
       document = Object.values(conditionalDocuments).find(doc => doc.DocumentID === documentId);
     }
     if (!document && documentId === otherDocuments.other.DocumentID) {
       document = otherDocuments.other;
     }
     // --- Modification End ---
     if (!document) return false;

     // Find *any* entry for this label marked as 'not_available'
     const docEntry = uploadedDocuments.find(
       doc => doc.documentName === document.documentName && doc.label === label && doc.status === 'not_available'
     );
     // Check if there's an entry and its status is 'not_available'
     return !!docEntry; // Return true if such an entry exists
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!businessName) {
      alert('Please enter a business name');
      return;
    }
    
    setIsLoading(true);
    try {
      // Update existing document (since it was already created)
      const prequalRef = doc(db, 'prequalificationApplications', initialData.id);
      await updateDoc(prequalRef, {
        businessName,
        purchasePrice: purchasePrice.replace(/[^0-9]/g, ''),
        businessAge,
        businessDescription,
        licenseNeeded,
        realEstateIncluded: realEstateIncluded === 'yes',
        realEstateValue: realEstateIncluded === 'yes' ? realEstateValue.replace(/[^0-9]/g, '') : '',
        hasOtherAddbacks: hasOtherAddbacks === 'yes', // Save addbacks status
        topCustomerConcentration: topCustomerConcentration === 'yes', // Modified to save boolean
        hasMultipleLLCs: hasMultipleLLCs === 'yes', // Save multiple LLCs status - Added
        status: 'In progress',
        lastUpdated: serverTimestamp(),
        // --- Refactor: Remove documents array ---
        // documents: uploadedDocuments // NO LONGER SAVED HERE
      });
      
      alert('Prequalification application updated successfully!');
      onCancel(); // Return to list view
    } catch (error) {
      console.error('Error updating prequalification:', error);
      alert('Error updating prequalification. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePrequalification = async () => {
    if (!currentUser) return;
    
    try {
      const initialData = {
        businessName,
        purchasePrice: purchasePrice.replace(/[^0-9]/g, ''),
        businessAge,
        businessDescription,
        licenseNeeded,
        realEstateIncluded: realEstateIncluded === 'yes',
        realEstateValue: realEstateIncluded === 'yes' ? realEstateValue.replace(/[^0-9]/g, '') : '',
        hasOtherAddbacks: hasOtherAddbacks === 'yes', // Save addbacks status
        topCustomerConcentration: topCustomerConcentration === 'yes', // Modified to save boolean 
        hasMultipleLLCs: hasMultipleLLCs === 'yes', // Save multiple LLCs status - Added
        status: 'applicationstarted', // Updated initial status
        brokerId: currentUser.uid,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        // --- Refactor: Remove documents array ---
        // documents: [] // NO LONGER NEEDED HERE
      };
      
      const docRef = await addDoc(collection(db, 'prequalificationApplications'), initialData);
      console.log('Created new prequalification document with ID:', docRef.id);
      setPrequalId(docRef.id);
      
      // Add this ID to the broker's prequalification list
      const brokerRef = doc(db, 'brokers', currentUser.uid);
      await updateDoc(brokerRef, {
        prequalificationApplications: arrayUnion(docRef.id)
      });
      
      alert('Prequalification started successfully!');
    } catch (error) {
      console.error('Error creating prequalification document:', error);
      alert('Error starting prequalification. Please try again.');
    }
  };

  // Get document file URL
  const getDocumentFileUrl = (documentId, label) => {
    // --- Fix: Search both standard and conditional documents ---
    let document = businessDocuments.find(doc => doc.DocumentID === documentId);
    if (!document) {
      document = Object.values(conditionalDocuments).find(doc => doc.DocumentID === documentId);
    }
    if (!document && documentId === otherDocuments.other.DocumentID) {
      document = otherDocuments.other;
    }    
    // --- End Fix ---
    
    if (!document) return null; // Return null if definition not found in either list
    
    // Find the corresponding document in the fetched metadata state
    const uploadedDoc = uploadedDocuments.find(
      doc => doc.documentName === document.documentName && doc.label === label && doc.status === 'uploaded'
    );
    
    return uploadedDoc ? uploadedDoc.fileUrl : null;
  };

  useEffect(() => {
    console.log("BusinessPrequalificationForm initialData:", initialData);
    
    if (initialData && initialData.id) {
      console.log("Setting form data from initialData");
      setBusinessName(initialData.businessName || '');
      // Make sure purchasePrice is properly handled even if it's a number
      setPurchasePrice(initialData.purchasePrice ? formatCurrency(initialData.purchasePrice) : '');
      setBusinessAge(initialData.businessAge || '');
      setBusinessDescription(initialData.businessDescription || '');
      setLicenseNeeded(initialData.licenseNeeded || false);
      setPrequalId(initialData.id);
      
      // Set real estate fields from initialData
      if (initialData.realEstateIncluded !== undefined) {
        setRealEstateIncluded(initialData.realEstateIncluded ? 'yes' : 'no');
      } else {
        setRealEstateIncluded(null);
      }
      setRealEstateValue(initialData.realEstateValue ? formatCurrency(initialData.realEstateValue) : '');

      // Set addbacks status from initialData
      if (initialData.hasOtherAddbacks !== undefined) {
        setHasOtherAddbacks(initialData.hasOtherAddbacks ? 'yes' : 'no');
      } else {
        setHasOtherAddbacks(null);
      }

      // Set multiple LLCs status from initialData - Added
      if (initialData.hasMultipleLLCs !== undefined) {
        setHasMultipleLLCs(initialData.hasMultipleLLCs ? 'yes' : 'no');
      } else {
        setHasMultipleLLCs(null);
      }

      // Set customer concentration from initialData - Modified
      if (initialData.topCustomerConcentration !== undefined) {
        setTopCustomerConcentration(initialData.topCustomerConcentration ? 'yes' : 'no');
      } else {
        setTopCustomerConcentration(null);
      }

      // --- Refactor: Fetch metadata instead of using initialData.documents ---
      if (initialData.id) {
        fetchPrequalificationMetadata(initialData.id);
      } else {
        // Clear documents if there's no initial data or ID
        setUploadedDocuments([]);
      }
      // Remove the direct setting from initialData.documents
      // if (initialData.documents && Array.isArray(initialData.documents)) {
      //   console.log("Setting uploaded documents from initialData:", initialData.documents);
      //   setUploadedDocuments(initialData.documents); // NO LONGER DONE HERE
      // }
    } else {
       // Clear form and documents if no initialData
       setBusinessName('');
       setPurchasePrice('');
       setBusinessAge('');
       setBusinessDescription('');
       setLicenseNeeded(false);
       setPrequalId(null);
       setRealEstateIncluded(null);
       setRealEstateValue('');
       setHasOtherAddbacks(null);
       setHasMultipleLLCs(null);
       setTopCustomerConcentration(null);
       setUploadedDocuments([]); // Clear documents
    }
  }, [initialData]); // Add fetchPrequalificationMetadata dependency if needed, but it's called inside

  // Calculate progress percentage
  const calculateProgress = () => {
    let filledFields = 0;
    let totalFields = 5; // Keeping the same total fields count
    
    if (businessName) filledFields++;
    if (purchasePrice) filledFields++;
    if (businessAge) filledFields++;
    if (businessDescription) filledFields++;
    if (topCustomerConcentration !== null) filledFields++; // Changed to check if not null
    
    // Add document uploads to progress calculation
    // This part might need adjustment based on how 'completeness' is defined with the new structure.
    // For now, let's count distinct labels that are either 'uploaded' or 'not_available'.
    const requiredLabels = new Set();
    businessDocuments.forEach(doc => doc.Labels.forEach(label => requiredLabels.add(label)));
    if (licenseNeeded) conditionalDocuments.license.Labels.forEach(label => requiredLabels.add(label));
    if (realEstateIncluded === 'yes') conditionalDocuments.appraisal.Labels.forEach(label => requiredLabels.add(label));
    if (hasOtherAddbacks === 'yes') conditionalDocuments.addbacks.Labels.forEach(label => requiredLabels.add(label));
    if (topCustomerConcentration === 'yes') conditionalDocuments.customerConcentration.Labels.forEach(label => requiredLabels.add(label));
    // Add 'Other' if needed for progress calculation, or exclude it. Let's exclude for now.

    const completedDocs = new Set(
        uploadedDocuments
            .filter(doc => doc.status === 'uploaded' || doc.status === 'not_available')
            .map(doc => doc.label) // Assuming label uniquely identifies a required slot
    );
    
    const requiredDocCount = requiredLabels.size; // Count unique required labels
    const completedDocCount = completedDocs.size; // Count unique completed labels

    // Combine field progress and document progress (example: 50/50 weight)
    const fieldProgress = (filledFields / totalFields) * 50;
    const docProgress = requiredDocCount > 0 ? (completedDocCount / requiredDocCount) * 50 : 50; // Avoid division by zero, give 50% if no docs required?

    return Math.round(fieldProgress + docProgress);
    // Old calculation based on uploadedDocuments.length:
    // const totalDocLabels = businessDocuments.reduce((sum, doc) => sum + doc.Labels.length, 0);
    // const uploadedLabels = uploadedDocuments.length; // This is no longer accurate for progress
    // return Math.round((filledFields / totalFields) * 100);
  };

  // Function to autosave the form data
  const autosaveFormData = async (field, value) => {
    if (!initialData || !initialData.id) {
      // If we don't have an ID yet, create the document first
      if (!prequalId) {
        await handleCreatePrequalification();
        return; // Return as handleCreatePrequalification will set prequalId, and we'll trigger field updates on subsequent changes
      }
    }
    
    const docId = initialData?.id || prequalId;
    if (!docId) return;
    
    setAutoSaving(true);
    try {
      const prequalRef = doc(db, 'prequalificationApplications', docId);
      const updateData = { [field]: value, lastUpdated: serverTimestamp() };
      
      // For currency fields, remove formatting before saving
      if (field === 'purchasePrice' || field === 'realEstateValue') {
        updateData[field] = value.replace(/[^0-9]/g, '');
      }
      
      // For boolean fields from radio buttons
      if (field === 'realEstateIncluded') updateData[field] = value === 'yes';
      if (field === 'hasOtherAddbacks') updateData[field] = value === 'yes';
      if (field === 'topCustomerConcentration') updateData[field] = value === 'yes';
      if (field === 'hasMultipleLLCs') updateData[field] = value === 'yes';
      
      await updateDoc(prequalRef, updateData);
    } catch (error) {
      console.error(`Error autosaving field ${field}:`, error);
      showNotification('error', `Failed to save ${field}. Please try manually saving.`);
    } finally {
      setAutoSaving(false);
    }
  };

  // Function to handle field changes with debounced autosave
  const handleFieldChange = (field, value) => {
    // Update local state immediately
    switch (field) {
      case 'businessName': setBusinessName(value); break;
      case 'purchasePrice': setPurchasePrice(formatCurrency(value)); break;
      case 'businessAge': setBusinessAge(value); break;
      case 'businessDescription': setBusinessDescription(value); break;
      case 'licenseNeeded': setLicenseNeeded(value); break;
      case 'realEstateIncluded': setRealEstateIncluded(value); break;
      case 'realEstateValue': setRealEstateValue(formatCurrency(value)); break;
      case 'hasOtherAddbacks': setHasOtherAddbacks(value); break;
      case 'topCustomerConcentration': setTopCustomerConcentration(value); break;
      case 'hasMultipleLLCs': setHasMultipleLLCs(value); break;
      default: break;
    }
    
    // Clear existing timer
    if (saveTimer) clearTimeout(saveTimer);
    
    // Set new timer for autosave
    const newTimer = setTimeout(() => {
      autosaveFormData(field, value);
    }, 1000); // 1 second debounce
    
    setSaveTimer(newTimer);
  };

  // Helper to get all currently required document labels
  const getRequiredDocumentLabels = () => {
    const requiredLabels = new Set();

    // Standard business documents
    businessDocuments.forEach(docDef => {
      // Handle tax returns (IDs 1, 2, 3) based on multiple LLCs
      if ([1, 2, 3].includes(docDef.DocumentID)) {
        if (docDef.Labels && docDef.Labels.length > 0) {
          requiredLabels.add(docDef.Labels[0]); // Always add the first label
        }
        if (hasMultipleLLCs === 'yes' && docDef.Labels && docDef.Labels.length > 1 && docDef.Labels[1].startsWith('Set 2')) {
          requiredLabels.add(docDef.Labels[1]); // Add the second "Set 2" label if multiple LLCs and label exists
        }
      } else {
        // For other standard documents, add all their labels
        docDef.Labels.forEach(label => requiredLabels.add(label));
      }
    });

    // Conditional documents
    if (licenseNeeded && conditionalDocuments.license) {
      conditionalDocuments.license.Labels.forEach(label => requiredLabels.add(label));
    }
    if (realEstateIncluded === 'yes' && conditionalDocuments.appraisal) {
      conditionalDocuments.appraisal.Labels.forEach(label => requiredLabels.add(label));
    }
    if (hasOtherAddbacks === 'yes' && conditionalDocuments.addbacks) {
      conditionalDocuments.addbacks.Labels.forEach(label => requiredLabels.add(label));
    }
    if (topCustomerConcentration === 'yes' && conditionalDocuments.customerConcentration) {
      conditionalDocuments.customerConcentration.Labels.forEach(label => requiredLabels.add(label));
    }

    // Note: 'otherDocuments.other' is typically not "required" for submission, so not included here.
    // If it were, you would add its labels:
    // otherDocuments.other.Labels.forEach(label => requiredLabels.add(label));

    return Array.from(requiredLabels);
  };

  // Helper to find document definition by DocumentID
  const findDocumentDefinitionById = (documentId) => {
    let docDef = businessDocuments.find(d => d.DocumentID === documentId);
    if (!docDef) {
      docDef = Object.values(conditionalDocuments).find(d => d.DocumentID === documentId);
    }
    if (!docDef && documentId === otherDocuments.other.DocumentID) {
      docDef = otherDocuments.other;
    }
    return docDef;
  };
  
  // Helper to find document definition by Label (more complex, as labels are not unique across DocumentIDs)
  // This simplified version assumes we can iterate and find the first match, suitable if labels are unique within their context of requirement.
  const findDocumentDefinitionByLabel = (labelToFind) => {
    const allDocDefinitions = [
      ...businessDocuments,
      ...(licenseNeeded ? [conditionalDocuments.license] : []),
      ...(realEstateIncluded === 'yes' ? [conditionalDocuments.appraisal] : []),
      ...(hasOtherAddbacks === 'yes' ? [conditionalDocuments.addbacks] : []),
      ...(topCustomerConcentration === 'yes' ? [conditionalDocuments.customerConcentration] : []),
      // Do not include otherDocuments.other unless it's made mandatory
    ];

    for (const docDef of allDocDefinitions) {
      if (docDef.Labels.includes(labelToFind)) {
        // Special handling for tax returns if not multiple LLCs
        if ([1,2,3].includes(docDef.DocumentID) && labelToFind.startsWith('Set 2') && hasMultipleLLCs !== 'yes') {
          continue; // Skip "Set 2" label if not multiple LLCs
        }
        return docDef;
      }
    }
    return null; // Should not happen if label comes from getRequiredDocumentLabels
  };


  // Function to check if all requirements for submission are met
  const checkAllRequirementsMet = () => {
    const missingFields = [];
    const missingDocuments = [];

    // 1. Check form fields
    if (!businessName.trim()) missingFields.push("Business Name");
    if (realEstateIncluded === null) missingFields.push("Real Estate Included selection");
    if (realEstateIncluded === 'yes' && !realEstateValue.replace(/\D/g, '')) missingFields.push("Value of Real Estate");
    if (hasOtherAddbacks === null) missingFields.push("Other Addbacks selection");
    if (hasMultipleLLCs === null) missingFields.push("Multiple LLCs selection");
    if (topCustomerConcentration === null) missingFields.push("Top Customer Concentration selection");

    // 2. Check documents
    const requiredLabels = getRequiredDocumentLabels();
    for (const label of requiredLabels) {
      const docDefinition = findDocumentDefinitionByLabel(label); // Find which DocumentID this label belongs to
      if (docDefinition) {
        const isUp = isDocumentUploaded(docDefinition.DocumentID, label);
        const isNA = isDocumentNotAvailable(docDefinition.DocumentID, label);
        if (!isUp && !isNA) {
          missingDocuments.push(label);
        }
      } else {
        // This case should ideally not be reached if getRequiredDocumentLabels is correct
        console.warn(`Could not find document definition for required label: ${label}`);
        missingDocuments.push(`${label} (definition not found)`);
      }
    }
    
    const allMet = missingFields.length === 0 && missingDocuments.length === 0;
    return { allMet, missingFields, missingDocuments };
  };

  // Effect to update isSubmittable state
  useEffect(() => {
    const { allMet } = checkAllRequirementsMet();
    // Application must also have an ID (been saved at least once) to be submittable
    const currentPrequalId = initialData?.id || prequalId;
    setIsSubmittable(allMet && !!currentPrequalId);

  }, [
    businessName, purchasePrice, businessAge, businessDescription, licenseNeeded, 
    realEstateIncluded, realEstateValue, hasOtherAddbacks, hasMultipleLLCs, 
    topCustomerConcentration, uploadedDocuments, initialData, prequalId // Listen to prequalId too
  ]);

  const handleSubmitApplication = async () => {
    setIsSubmitting(true);
    const currentPrequalId = initialData?.id || prequalId;

    if (!currentPrequalId) {
      showNotification('error', 'Please save the application before submitting.');
      setIsSubmitting(false);
      return;
    }

    const { allMet, missingFields, missingDocuments } = checkAllRequirementsMet();

    if (!allMet) {
      let message = "Please complete all required items before submitting: ";
      if (missingFields.length > 0) {
        message += `\nFields: ${missingFields.join(', ')}. `;
      }
      if (missingDocuments.length > 0) {
        message += `\nDocuments: ${missingDocuments.join(', ')}.`;
      }
      // Replace \n with <br /> for HTML display in notification, or use a multi-line supporting notification component
      showNotification('error', message.replace(/\n/g, '<br/>'));
      setIsSubmitting(false);
      return;
    }

    try {
      const prequalRef = doc(db, 'prequalificationApplications', currentPrequalId);
      await updateDoc(prequalRef, {
        businessName,
        purchasePrice: purchasePrice.replace(/[^0-9]/g, ''),
        businessAge,
        businessDescription,
        licenseNeeded,
        realEstateIncluded: realEstateIncluded === 'yes',
        realEstateValue: realEstateIncluded === 'yes' ? realEstateValue.replace(/[^0-9]/g, '') : '',
        hasOtherAddbacks: hasOtherAddbacks === 'yes',
        topCustomerConcentration: topCustomerConcentration === 'yes',
        hasMultipleLLCs: hasMultipleLLCs === 'yes',
        status: 'underReview', // Update status
        lastUpdated: serverTimestamp(),
        // documents array is no longer directly managed here
      });
      
      showNotification('success', 'Application submitted successfully for review!');
      onCancel(); // Return to list view
    } catch (error) {
      console.error('Error submitting prequalification:', error);
      showNotification('error', 'Error submitting prequalification. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div className="bg-gradient-to-br from-white to-indigo-50 rounded-xl shadow-xl overflow-hidden">
        {/* Header with progress bar */}
        <div className="relative bg-indigo-700 px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-3xl font-bold text-white">
                Business Prequalification
              </h2>
              <p className="text-indigo-200 mt-1">
                Complete this form to prequalify a business for an SBA loan
              </p>
            </div>
            <button
              onClick={onCancel}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-700"
            >
              Cancel
            </button>
          </div>
          
          {/* Progress bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-indigo-100">Completion Progress</span>
              <span className="text-sm font-medium text-indigo-100">{calculateProgress()}%</span>
            </div>
            <div className="w-full bg-indigo-900 rounded-full h-2.5">
              <div 
                className="bg-indigo-300 h-2.5 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${calculateProgress()}%` }}
              ></div>
            </div>
          </div>
        </div>
        
        {/* Steps navigation */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-8 py-4">
            <nav className="flex">
              <button 
                onClick={() => setActiveStep(1)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg mr-2 ${
                  activeStep === 1 
                    ? 'bg-indigo-100 text-indigo-700 border-b-2 border-indigo-700' 
                    : 'text-gray-500 hover:text-indigo-600 hover:bg-indigo-50'
                }`}
              >
                1. Business Information
              </button>
              <button 
                onClick={() => {
                  // Basic validation before proceeding to documents
                  if (!businessName || realEstateIncluded === null || hasOtherAddbacks === null) {
                    alert("Please complete all required fields in Business Information first (Business Name, Real Estate Inclusion, Other Addbacks).");
                    return;
                  }
                  // Additional validation for real estate value if included
                  if (realEstateIncluded === 'yes' && !realEstateValue) {
                     alert("Please enter the Value of Real Estate.");
                     return;
                  }
                  setActiveStep(2);
                }}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                  activeStep === 2 
                    ? 'bg-indigo-100 text-indigo-700 border-b-2 border-indigo-700' 
                    : 'text-gray-500 hover:text-indigo-600 hover:bg-indigo-50'
                }`}
              >
                2. Required Documents
              </button>
            </nav>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-8">
          {/* Step 1: Business Information */}
          {activeStep === 1 && (
            <div className="space-y-8 max-w-5xl mx-auto">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                  <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full mr-3 text-sm font-bold">1</span>
                  Business Details
                  {autoSaving && (
                    <span className="ml-3 text-xs font-normal text-indigo-600 flex items-center">
                      <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Autosaving...
                    </span>
                  )}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <label htmlFor="business-name" className="block text-sm font-medium text-gray-700 mb-1">
                      Business Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="business-name"
                      value={businessName}
                      onChange={(e) => handleFieldChange('businessName', e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Enter business name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="purchase-price" className="block text-sm font-medium text-gray-700 mb-1">
                      Total Purchase price (Including real estate)
                    </label>
                    <div className="relative rounded-md shadow-sm">
                      <input
                        type="text"
                        id="purchase-price"
                        value={purchasePrice}
                        onChange={(e) => handleFieldChange('purchasePrice', e.target.value)}
                        className="block w-full rounded-md border-gray-300 pl-7 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="$0"
                      />
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="business-age" className="block text-sm font-medium text-gray-700 mb-1">
                      Business Age
                    </label>
                    <input
                      type="text"
                      id="business-age"
                      value={businessAge}
                      onChange={(e) => handleFieldChange('businessAge', e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Enter business age (e.g., 5 years)"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label htmlFor="business-description" className="block text-sm font-medium text-gray-700 mb-1">
                      Business Description (May Skip If covered in CIM) 
                    </label>
                    <textarea
                      id="business-description"
                      value={businessDescription}
                      onChange={(e) => handleFieldChange('businessDescription', e.target.value)}
                      rows={8}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Provide a concise overview of the business, including its core operations, industry sector, and key differentiators. If a Confidential Information Memorandum (CIM) is being uploaded, this section may be used to summarize crucial points or add supplementary details not included in the CIM."
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <div className="flex items-center">
                      <input
                        id="license-needed"
                        type="checkbox"
                        checked={licenseNeeded}
                        onChange={(e) => handleFieldChange('licenseNeeded', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label htmlFor="license-needed" className="ml-2 block text-sm font-medium text-gray-700">
                        License needed for this business?
                      </label>
                    </div>
                  </div>

                  {/* Real Estate Question */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Is there real estate included in the deal? <span className="text-red-500">*</span>
                    </label>
                    <fieldset className="mt-2">
                      <legend className="sr-only">Real estate included</legend>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <input
                            id="real-estate-yes"
                            name="real-estate-included"
                            type="radio"
                            value="yes"
                            checked={realEstateIncluded === 'yes'}
                            onChange={(e) => handleFieldChange('realEstateIncluded', e.target.value)}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="real-estate-yes" className="ml-3 block text-sm font-medium text-gray-700">
                            Yes
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            id="real-estate-no"
                            name="real-estate-included"
                            type="radio"
                            value="no"
                            checked={realEstateIncluded === 'no'}
                            onChange={(e) => handleFieldChange('realEstateIncluded', e.target.value)}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="real-estate-no" className="ml-3 block text-sm font-medium text-gray-700">
                            No
                          </label>
                        </div>
                      </div>
                    </fieldset>
                  </div>

                  {/* Conditional Real Estate Value Input */}
                  {realEstateIncluded === 'yes' && (
                    <div>
                      <label htmlFor="real-estate-value" className="block text-sm font-medium text-gray-700 mb-1">
                        Value of Real Estate <span className="text-red-500">*</span>
                      </label>
                      <div className="relative rounded-md shadow-sm">
                        <input
                          type="text"
                          id="real-estate-value"
                          value={realEstateValue}
                          onChange={(e) => handleFieldChange('realEstateValue', e.target.value)}
                          className="block w-full rounded-md border-gray-300 pl-7 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="$0"
                          required
                        />
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Multiple LLCs Question - Added */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Does the business have more than one LLC / set of tax returns?
                      <span className="text-red-500">*</span>
                    </label>
                    <fieldset className="mt-2">
                      <legend className="sr-only">Multiple LLCs</legend>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <input
                            id="multiple-llc-yes"
                            name="multiple-llc-included"
                            type="radio"
                            value="yes"
                            checked={hasMultipleLLCs === 'yes'}
                            onChange={(e) => handleFieldChange('hasMultipleLLCs', e.target.value)}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="multiple-llc-yes" className="ml-3 block text-sm font-medium text-gray-700">
                            Yes
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            id="multiple-llc-no"
                            name="multiple-llc-included"
                            type="radio"
                            value="no"
                            checked={hasMultipleLLCs === 'no'}
                            onChange={(e) => handleFieldChange('hasMultipleLLCs', e.target.value)}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="multiple-llc-no" className="ml-3 block text-sm font-medium text-gray-700">
                            No
                          </label>
                        </div>
                      </div>
                    </fieldset>
                     {hasMultipleLLCs === 'yes' && (
                       <p className="mt-2 text-sm text-indigo-700">
                         Please provide documents for both sets in Step 2.
                       </p>
                     )}
                  </div>

                  {/* Addbacks Question */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Are there any addbacks besides Interest, Depreciation, Amortization and Officer's Compensation? <span className="text-red-500">*</span>
                    </label>
                    <fieldset className="mt-2">
                      <legend className="sr-only">Other Addbacks Included</legend>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <input
                            id="addbacks-yes"
                            name="other-addbacks-included"
                            type="radio"
                            value="yes"
                            checked={hasOtherAddbacks === 'yes'}
                            onChange={(e) => handleFieldChange('hasOtherAddbacks', e.target.value)}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="addbacks-yes" className="ml-3 block text-sm font-medium text-gray-700">
                            Yes
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            id="addbacks-no"
                            name="other-addbacks-included"
                            type="radio"
                            value="no"
                            checked={hasOtherAddbacks === 'no'}
                            onChange={(e) => handleFieldChange('hasOtherAddbacks', e.target.value)}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="addbacks-no" className="ml-3 block text-sm font-medium text-gray-700">
                            No
                          </label>
                        </div>
                      </div>
                    </fieldset>
                     {hasOtherAddbacks === 'yes' && (
                       <p className="mt-2 text-sm text-indigo-700">
                         Please upload a document outlining these addbacks in Step 2. Addbacks must be verifiable.
                       </p>
                     )}
                  </div>

                  {/* Top 3 Customers Question */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Do the top 3 customers make up more than 15% of revenue? <span className="text-red-500">*</span>
                    </label>
                    <fieldset className="mt-2">
                      <legend className="sr-only">Customer Concentration</legend>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <input
                            id="customer-concentration-yes"
                            name="customer-concentration"
                            type="radio"
                            value="yes"
                            checked={topCustomerConcentration === 'yes'}
                            onChange={(e) => handleFieldChange('topCustomerConcentration', e.target.value)}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="customer-concentration-yes" className="ml-3 block text-sm font-medium text-gray-700">
                            Yes
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            id="customer-concentration-no"
                            name="customer-concentration"
                            type="radio"
                            value="no"
                            checked={topCustomerConcentration === 'no'}
                            onChange={(e) => handleFieldChange('topCustomerConcentration', e.target.value)}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="customer-concentration-no" className="ml-3 block text-sm font-medium text-gray-700">
                            No
                          </label>
                        </div>
                      </div>
                    </fieldset>
                    {topCustomerConcentration === 'yes' && (
                      <p className="mt-2 text-sm text-indigo-700">
                        Please upload a document in Step 2 listing your top 10 customers by percentage of revenue.
                      </p>
                    )}
                  </div>

                </div>
              </div>
              
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={onCancel}
                  className="rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                     // Basic validation before proceeding
                     if (!businessName || realEstateIncluded === null || hasOtherAddbacks === null) {
                       alert("Please complete all required fields in Business Information first (Business Name, Real Estate Inclusion, Other Addbacks).");
                       return;
                     }
                     // Additional validation for real estate value if included
                     if (realEstateIncluded === 'yes' && !realEstateValue) {
                        alert("Please enter the Value of Real Estate.");
                        return;
                     }
                     setActiveStep(2);
                  }}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Continue to Documents
                </button>
              </div>
            </div>
          )}
          
          {/* Step 2: Required Documents */}
          {activeStep === 2 && (
            <div className="space-y-8 max-w-5xl mx-auto">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                  <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full mr-3 text-sm font-bold">2</span>
                  Required Documents
                </h3>
                
                {/* Document sections without accordion */}
                <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg shadow-sm bg-white">
                  {[
                    ...businessDocuments,
                    ...(licenseNeeded ? [conditionalDocuments.license] : []),
                    ...(realEstateIncluded === 'yes' ? [conditionalDocuments.appraisal] : []),
                    ...(hasOtherAddbacks === 'yes' ? [conditionalDocuments.addbacks] : []),
                    ...(topCustomerConcentration === 'yes' ? [conditionalDocuments.customerConcentration] : []),
                    otherDocuments.other // Include the new document type here
                  ].map((document, index) => {
                    // Calculate uploaded count for documents with multiple labels
                    // For single-label docs (like the updated 2024 Financials), this logic still works.
                    const completedCount = document.Labels.reduce((count, label) => {
                      const isUp = isDocumentUploaded(document.DocumentID, label);
                      const isNA = isDocumentNotAvailable(document.DocumentID, label);
                      return count + ((isUp || isNA) ? 1 : 0);
                    }, 0);
                    const totalLabels = document.Labels.length;
                    const isComplete = completedCount === totalLabels;

                    return (
                      <div key={document.DocumentID} className="bg-white first:rounded-t-lg last:rounded-b-lg">
                        {/* Document Section */}
                        <div className="px-6 py-4">
                          {/* --- Modification Start: Conditional Label Rendering --- */}
                          {document.Labels
                            .filter((label, index) => {
                              // For docs 1, 2, 3:
                              if ([1, 2, 3].includes(document.DocumentID)) {
                                // Always show the first label (index 0)
                                if (index === 0) {
                                  return true;
                                }
                                // Show the second label (index 1, starts with "Set 2") only if multiple LLCs is 'yes'
                                if (index === 1 && label.startsWith('Set 2') && hasMultipleLLCs === 'yes') {
                                  return true;
                                }
                                // Otherwise, hide the second label for docs 1, 2, 3
                                return false;
                              }
                              // For all other documents, show all their labels
                              return true;
                            })
                            .map((label) => {
                          {/* --- Modification End --- */}

                            const uploadKey = `${document.DocumentID}-${label}`;
                            const currentStatus = uploadStatuses[uploadKey] || 'idle';
                            const isUploaded = isDocumentUploaded(document.DocumentID, label);
                            const fileUrl = getDocumentFileUrl(document.DocumentID, label);
                            const isNA = isDocumentNotAvailable(document.DocumentID, label);

                            // --- Modification Start: Handle multiple files display for 'Other' ---
                            const isOtherDocType = document.DocumentID === otherDocuments.other.DocumentID;
                            const otherUploadedFiles = isOtherDocType
                              ? uploadedDocuments.filter(
                                  doc =>
                                    doc.documentName === document.documentName &&
                                    doc.label === label &&
                                    doc.status === 'uploaded'
                                )
                              : [];
                            // --- Modification End ---

                            return (
                              <div key={label} className="py-4">
                                {/* Wrapper div for label and action buttons */} 
                                <div 
                                  className={`flex items-center justify-between p-4 rounded-lg border transition-all duration-200 ${ 
                                    isNA ? 'bg-gray-50 border-gray-200' : 
                                    currentStatus === 'uploading' ? 'bg-blue-50 border-blue-200 animate-pulse' : 
                                    isUploaded ? 'bg-emerald-50 border-emerald-200' : // Keep green background if *any* file is uploaded for 'Other'
                                    'bg-white border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/30' // Subtle indigo hover
                                  } ${ 
                                    !isNA && currentStatus !== 'uploading' ? 'hover:shadow-sm' : '' 
                                  }`}
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    if (!isNA && currentStatus !== 'uploading') {
                                      e.currentTarget.classList.add('border-indigo-300', 'bg-indigo-50/30', 'shadow-sm'); // Indigo highlight on drag
                                    }
                                  }}
                                  onDragLeave={(e) => {
                                    e.preventDefault();
                                    if (!isNA && currentStatus !== 'uploading') {
                                      e.currentTarget.classList.remove('border-indigo-300', 'bg-indigo-50/30', 'shadow-sm');
                                    }
                                  }}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    if (!isNA && currentStatus !== 'uploading') {
                                      e.currentTarget.classList.remove('border-indigo-300', 'bg-indigo-50/30', 'shadow-sm');
                                      const file = e.dataTransfer.files[0];
                                      if (file) {
                                        const fakeEvent = { target: { files: [file] } };
                                        handleFileUpload(fakeEvent, document.DocumentID, label);
                                      }
                                    }
                                  }}
                                >
                                  <label
                                    htmlFor={`file-${document.DocumentID}-${label}`}
                                    className={`flex-1 flex items-center ${isNA || currentStatus === 'uploading' ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                                  >
                                    <div className="flex items-center">
                                      {/* Icon changes based on state */}
                                      {isUploaded ? (
                                        <CheckCircleIcon className="h-6 w-6 mr-3 flex-shrink-0 text-emerald-500" />
                                      ) : currentStatus === 'uploading' ? (
                                        <CloudArrowUpIcon className="h-6 w-6 mr-3 flex-shrink-0 text-blue-500 animate-bounce" />
                                      ) : (
                                        <DocumentTextIcon className={`h-6 w-6 mr-3 flex-shrink-0 ${ 
                                          isNA ? 'text-gray-400' : 'text-indigo-500' // Subtle indigo icon default
                                        }`} />
                                      )}
                                      <div>
                                        <span className={`text-sm font-medium ${ 
                                          isNA ? 'text-gray-500' : 
                                          currentStatus === 'uploading' ? 'text-blue-700' : 
                                          isUploaded ? 'text-emerald-700' : 
                                          'text-gray-800' // Slightly darker default text
                                        }`}>
                                          {label}
                                        </span>
                                        {/* Status text indicators */}
                                        {currentStatus === 'uploading' && <span className="text-xs ml-2 text-blue-600">(Uploading...)</span>}
                                        {/* Don't show filename here for 'Other' docs; it's listed below */}
                                        {!isOtherDocType && isUploaded && (
                                          <span className="text-xs ml-2 text-emerald-600">
                                            ({uploadedDocuments.find(doc => 
                                              doc.documentName === getGeneralDocumentName(document) && 
                                              doc.label === label && 
                                              doc.status === 'uploaded'
                                            )?.fileName || 'Uploaded'})
                                          </span>
                                        )}
                                        {isNA && <span className="text-xs ml-2 text-gray-500">(Not Available)</span>}
                                      </div>
                                    </div>
                                  </label>

                                  {/* Action Buttons */}
                                  <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                                    {/* View Button (Only for single-file types) */}
                                    {!isOtherDocType && fileUrl && isUploaded && (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.preventDefault(); window.open(fileUrl, '_blank'); }}
                                        className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                                      >
                                        View
                                      </button>
                                    )}
                                    {/* Upload Button (Always show for 'Other' if not N/A, conditionally for others) */}
                                    {(!isNA && !isUploaded || isOtherDocType) && currentStatus !== 'uploading' && (
                                      <label
                                        htmlFor={`file-${document.DocumentID}-${label}`}
                                        className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors cursor-pointer"
                                      >
                                        {/* Change button text for 'Other' docs if files already exist */}
                                        {isOtherDocType && isUploaded ? 'Upload More' : 'Upload'}
                                      </label>
                                    )}
                                    {/* N/A Button */}
                                    {!isNA && currentStatus !== 'uploading' && (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.preventDefault(); handleDoNotHave(document.DocumentID, label); }}
                                        className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                                      >
                                        N/A
                                      </button>
                                    )}
                                    {/* Enable Button */}
                                    {isNA && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setUploadStatuses(prev => { const newState = { ...prev }; delete newState[uploadKey]; return newState; });
                                          // --- Modification Start: Clear N/A marker for 'Other' ---
                                          // We need to remove the N/A document from the Firestore collection and refetch
                                          const docToRemove = uploadedDocuments.find(doc => doc.documentName === document.documentName && doc.label === label && doc.status === 'not_available'); // Match using document.documentName
                                          if (docToRemove && docToRemove.id) {
                                            // --- Finalized: Uncomment and implement delete logic --- 
                                            deleteDoc(doc(db, 'prequalificationMetadata', docToRemove.id))
                                              .then(() => {
                                                showNotification('info', `'${label}' marked as available again.`);
                                                fetchPrequalificationMetadata(prequalId); // Refresh state after delete
                                              })
                                              .catch(err => {
                                                console.error("Error removing N/A marker:", err);
                                                showNotification('error', 'Failed to re-enable document.');
                                                // Optional: Revert local status if delete failed
                                                setUploadStatuses(prev => ({ ...prev, [uploadKey]: 'not_available' })); 
                                              });
                                            // --- End Finalized ---
                                            // Removed previous console.warn and local optimistic update
                                          } else {
                                               showNotification('info', `'${label}' marked as available again (local state).`); // This case might indicate a state sync issue
                                          }
                                          // --- Modification End ---
                                        }}
                                        className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                                      >
                                        Re-enable
                                      </button>
                                    )}
                                  </div>

                                  <input
                                    id={`file-${document.DocumentID}-${label}`}
                                    type="file"
                                    className="hidden"
                                    onChange={(e) => handleFileUpload(e, document.DocumentID, label)}
                                    accept={document.AcceptedFileTypes || "*/*"}
                                    disabled={currentStatus === 'uploading' || isNA}
                                  />
                                </div>
                                  
                                {/* --- Modification Start: List uploaded files for 'Other' --- */}
                                {isOtherDocType && otherUploadedFiles.length > 0 && (
                                  <div className="mt-3 pl-10 pr-4 space-y-2">
                                    <p className="text-xs font-medium text-gray-600">Uploaded files:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                      {otherUploadedFiles.map((file, idx) => (
                                        <li key={idx} className="text-sm text-gray-800 flex justify-between items-center">
                                          <span>{file.fileName}</span>
                                          <button
                                            type="button"
                                            onClick={(e) => { e.preventDefault(); window.open(file.fileUrl, '_blank'); }}
                                            className="text-xs text-indigo-600 hover:text-indigo-800 underline"
                                          >
                                            View
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Navigation buttons */}
              <div className="flex justify-between border-t border-gray-200 pt-6">
                <button
                  type="button"
                  onClick={() => setActiveStep(1)}
                  className="rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Back to Business Info
                </button>
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={isLoading || isSubmitting} // Disable if submitting too
                    className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex items-center"
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      'Save Prequalification'
                    )}
                  </button>
                  {/* {{ edit_4 }} */}
                  <button
                    type="button"
                    onClick={handleSubmitApplication}
                    disabled={!isSubmittable || isLoading || isSubmitting}
                    className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      'Submit Application'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default PrequalificationsTab;